import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { Plus, PencilSimple, Trash, Buildings, MagnifyingGlass, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const TYPE_COLORS = { brand: 'bg-[var(--accent-light)] text-[var(--accent)]', agency: 'bg-[var(--warning-bg)] text-[var(--warning)]', creator: 'bg-[var(--success-bg)] text-[var(--success)]' };
const MARKETS = ['India', 'UAE', 'Saudi Arabia', 'Other GCC', 'Multiple'];

export default function ClientsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.tenant_type === 'admin' || user?.tenant_type === 'master';
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({ name: '', website: '', industry: 'E-commerce', monthly_budget: 0, workspace_type: 'brand', market: 'India', contact_email: '' });
  const [search, setSearch] = useState('');

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    try { const res = await clientsAPI.list(); setClients(res.data); }
    catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editClient) {
        await clientsAPI.update(editClient.id, { name: form.name, website: form.website, industry: form.industry, monthly_budget: form.monthly_budget });
        toast.success('Client updated');
      } else {
        await clientsAPI.create({ name: form.name, website: form.website, industry: form.industry, monthly_budget: form.monthly_budget });
        toast.success('Workspace created');
      }
      setDialogOpen(false); setEditClient(null);
      setForm({ name: '', website: '', industry: 'E-commerce', monthly_budget: 0, workspace_type: 'brand', market: 'India', contact_email: '' });
      loadClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workspace?')) return;
    try { await clientsAPI.delete(id); toast.success('Deleted'); loadClients(); }
    catch { toast.error('Failed to delete'); }
  };

  const openEdit = (c) => {
    setEditClient(c);
    setForm({ name: c.name, website: c.website, industry: c.industry, monthly_budget: c.monthly_budget, workspace_type: 'brand', market: 'India', contact_email: '' });
    setDialogOpen(true);
  };

  const filtered = clients.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.industry?.toLowerCase().includes(search.toLowerCase()) ||
    c.website?.toLowerCase().includes(search.toLowerCase())
  );

  const industries = ['E-commerce', 'SaaS', 'Healthcare', 'Finance', 'Education', 'Real Estate', 'Technology', 'Marketing', 'D2C Skincare', 'D2C Fashion', 'Food & Beverage', 'FinTech', 'EdTech', 'FMCG', 'B2B SaaS', 'Other'];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="clients-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-[var(--text-primary)] tracking-tight">
              {isAdmin ? 'Client Manager' : 'Clients'}
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {isAdmin ? 'Manage all brand, agency and creator workspaces' : 'Manage your marketing clients'}
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditClient(null); setForm({ name: '', website: '', industry: 'E-commerce', monthly_budget: 0, workspace_type: 'brand', market: 'India', contact_email: '' }); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                <Plus size={16} className="mr-2" /> {isAdmin ? 'Add Client' : 'Add Client'}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="client-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">{editClient ? 'Edit Client' : 'Create Workspace'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-[10px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Client / Workspace Name</label>
                  <Input data-testid="client-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="Brand name" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-[10px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Domain</label>
                  <Input data-testid="client-website-input" value={form.website} onChange={e => setForm({...form, website: e.target.value})}
                    placeholder="brandname.com" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Workspace Type</label>
                    <Select value={form.workspace_type} onValueChange={v => setForm({...form, workspace_type: v})}>
                      <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        <SelectItem value="brand" className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">Brand</SelectItem>
                        <SelectItem value="agency" className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">Agency</SelectItem>
                        <SelectItem value="creator" className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">Creator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="block text-[10px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Category</label>
                  <Select value={form.industry} onValueChange={v => setForm({...form, industry: v})}>
                    <SelectTrigger data-testid="client-industry-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                      {industries.map(i => <SelectItem key={i} value={i} className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[10px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Primary Market</label>
                  <Select value={form.market} onValueChange={v => setForm({...form, market: v})}>
                    <SelectTrigger className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                      {MARKETS.map(m => <SelectItem key={m} value={m} className="text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {isAdmin && (
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Contact Email</label>
                    <Input value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})}
                      placeholder="contact@brand.com" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                  </div>
                )}
                <div>
                  <label className="block text-[10px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Monthly Budget</label>
                  <Input data-testid="client-budget-input" type="number" value={form.monthly_budget}
                    onChange={e => setForm({...form, monthly_budget: parseFloat(e.target.value) || 0})}
                    placeholder="5000" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                </div>
                <Button data-testid="save-client-btn" onClick={handleSave} className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                  {editClient ? 'Update Client' : 'Create Workspace'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
          <Input data-testid="client-search-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search clients..." className="pl-9 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]" />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="nx-metric-card rounded-md text-center py-12">
            <Buildings size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
            <p className="text-sm text-[var(--text-primary)] mb-1">No workspaces yet</p>
            <p className="text-xs text-[var(--text-secondary)] mb-4">Add your first client workspace to get started</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
              <Plus size={16} className="mr-2" /> Add Client
            </Button>
          </div>
        ) : (
          <div className="nx-metric-card rounded-md overflow-x-auto">
            <table className="w-full text-[11px] font-mono border-collapse">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]">
                  <th className="text-left p-2.5 text-[9px] uppercase tracking-wider">Name</th>
                  <th className="text-left p-2.5 text-[9px] uppercase tracking-wider">Domain</th>
                  {isAdmin && <th className="text-left p-2.5 text-[9px] uppercase tracking-wider">Type</th>}
                  <th className="text-left p-2.5 text-[9px] uppercase tracking-wider">Category</th>
                  <th className="text-right p-2.5 text-[9px] uppercase tracking-wider">SoM Score</th>
                  <th className="text-center p-2.5 text-[9px] uppercase tracking-wider">Status</th>
                  <th className="text-right p-2.5 text-[9px] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} className="border-b border-[var(--border-light)] hover:bg-[var(--canvas-tertiary)]">
                    <td className="p-2.5 text-[var(--text-primary)] font-medium">{c.display_name || c.name}</td>
                    <td className="p-2.5 text-[var(--text-secondary)]">{c.website || '—'}</td>
                    {isAdmin && (
                      <td className="p-2.5">
                        <span className={`px-2 py-0.5 text-[9px] rounded-full font-mono ${TYPE_COLORS[c.workspace_type || 'brand'] || TYPE_COLORS.brand}`}>
                          {(c.workspace_type || 'brand').charAt(0).toUpperCase() + (c.workspace_type || 'brand').slice(1)}
                        </span>
                      </td>
                    )}
                    <td className="p-2.5"><span className="nx-tag nx-tag-blue text-[8px]">{c.industry}</span></td>
                    <td className="p-2.5 text-right">
                      {c.seo_score ? (
                        <span className={`font-bold ${c.seo_score >= 70 ? 'text-[var(--success)]' : c.seo_score >= 50 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>{c.seo_score}%</span>
                      ) : <span className="text-[var(--text-tertiary)]">—</span>}
                    </td>
                    <td className="p-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 text-[9px] ${c.status === 'active' ? 'text-[var(--success)]' : 'text-[var(--text-tertiary)]'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'active' ? 'bg-[var(--success)]' : 'bg-[var(--text-tertiary)]'}`} />
                        {c.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Milestone 6.1 (Explicit Workspace Context) --
                             real navigation now, not a toast. Ownership
                             is verified server-side (POST
                             /workspace-context/enter, via the same
                             get_verified_client every client-scoped
                             route uses) when the destination route
                             loads -- this button never grants access
                             itself, it only takes you to the door. */}
                        {isAdmin && (
                          <button data-testid={`view-client-${c.id}`} onClick={() => navigate(`/workspace/${c.id}`)}
                            className="p-1.5 rounded hover:bg-[var(--canvas-tertiary)] text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors" title="Open workspace">
                            <Eye size={14} />
                          </button>
                        )}
                        <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)}
                          className="p-1.5 rounded hover:bg-[var(--canvas-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors" title="Edit">
                          <PencilSimple size={14} />
                        </button>
                        <button data-testid={`delete-client-${c.id}`} onClick={() => handleDelete(c.id)}
                          className="p-1.5 rounded hover:bg-[var(--error-bg)] text-[var(--text-secondary)] hover:text-[var(--error)] transition-colors" title="Delete">
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
